import React, { useMemo } from 'react'
import { AILoadingContext } from 'components/TasksV2/AITaskCreation/AIFormStepContext'
import {
  useAISubtaskGeneration,
  AISubtaskType,
  IAIGeneratedSubtask,
} from 'components/TasksV2/AITaskCreation/hooks/useAISubtaskGeneration'

interface IProps {
  children: React.ReactNode
  /** Fixed subtask type — the VM sub-flow always enhances an image subtask. */
  subtaskType?: AISubtaskType
  onGenerated: (result: IAIGeneratedSubtask, referenceImages: string[], subtaskType: AISubtaskType) => void
  promptSubmittedEvent?: string
  generatedEvent?: string
}

const noop = () => {}

/**
 * Supplies `AILoadingContext` outside of `AIFormStepProvider`, so the AI prompt/subtask steps can be
 * reused inside another flow (VM task creation). Only generation is provided here — submitting is
 * owned by the host flow.
 *
 * Must be mounted ABOVE the step switch: mounting it per-step would reset `isGenerating` when the
 * user moves from the prompt step to the subtask step, and the loader would never show.
 */
export const AIGenerationProvider: React.FC<IProps> = ({
  children,
  subtaskType = 'image',
  onGenerated,
  promptSubmittedEvent,
  generatedEvent,
}) => {
  const { isGenerating, generateFromPrompt } = useAISubtaskGeneration({
    getSubtaskType: () => subtaskType,
    onGenerated,
    promptSubmittedEvent,
    generatedEvent,
  })

  const value = useMemo(
    () => ({
      isSubmitting: false,
      isGenerating,
      selectedSubtaskType: subtaskType,
      setSelectedSubtaskType: noop,
      generateFromPrompt,
    }),
    [isGenerating, subtaskType, generateFromPrompt]
  )

  return <AILoadingContext.Provider value={value}>{children}</AILoadingContext.Provider>
}
